import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { BuildingsIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageContent } from '@/components/page-content';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/section-header';
import { organizationsOptions } from '@/hooks/query-options';
import { useAppForm } from '@/hooks/use-app-form';
import { authClient, useSession } from '@/lib/auth-client';

export const Route = createFileRoute(
  '/_auth/_side-layout/settings_/organization/details',
)({
  component: () => (
    <PageContent>
      <OrganizationDetails />
    </PageContent>
  ),
});

const orgSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  slug: z
    .string()
    .min(2, 'Slug must be at least 2 characters.')
    .regex(/^[a-z0-9-]+$/, 'Only lowercase letters, numbers, and hyphens.'),
});

// The "Details" tab: org name/slug. Resolves the active org from the session.
function OrganizationDetails() {
  const { data: session } = useSession();
  const activeOrganizationId = session?.session?.activeOrganizationId ?? null;
  const { data: organizations } = useQuery({
    ...(session?.user?.id
      ? organizationsOptions({ userId: session.user.id })
      : organizationsOptions({ userId: '' })),
    enabled: !!session?.user?.id,
  });
  const activeOrg =
    organizations?.find(
      (organization) => organization.id === activeOrganizationId,
    ) ?? null;

  if (!activeOrg) {
    return (
      <p className="text-muted-foreground">No active organization selected.</p>
    );
  }

  return <OrganizationSettingsForm activeOrg={activeOrg} />;
}

function OrganizationSettingsForm({
  activeOrg,
}: {
  activeOrg: { name: string; slug: string };
}) {
  const queryClient = useQueryClient();
  const form = useAppForm({
    defaultValues: {
      name: activeOrg.name,
      slug: activeOrg.slug,
    },
    validators: { onSubmit: orgSchema },
    onSubmit: async ({ value }) => {
      const { error } = await authClient.organization.update({
        data: { name: value.name, slug: value.slug },
      });
      if (error) {
        const isSlugConflict =
          error.status === 409 || error.message?.toLowerCase().includes('slug');
        toast.error(
          isSlugConflict
            ? 'Slug already taken'
            : 'Failed to update organization',
          {
            description: isSlugConflict
              ? 'Another organization is using that slug. Choose a different one.'
              : (error.message ?? 'Please try again.'),
          },
        );
        return;
      }
      form.reset({ name: value.name, slug: value.slug });
      await queryClient.invalidateQueries();
      toast.success('Organization updated');
    },
  });

  return (
    <Card>
      <CardContent className="space-y-6">
        <SectionHeader
          icon={<BuildingsIcon size={20} />}
          title="Organization Details"
          subTitle="Your organization's display name and URL slug."
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <form.AppForm>
            <form.AppField name="name">
              {(field) => (
                <field.Input label="Name" placeholder="My Organization" />
              )}
            </form.AppField>
            <form.AppField name="slug">
              {(field) => (
                <field.Input
                  label="Slug"
                  description="Only lowercase letters, numbers, and hyphens."
                  placeholder="my-organization"
                />
              )}
            </form.AppField>
            <form.SubmitButton />
          </form.AppForm>
        </form>
      </CardContent>
    </Card>
  );
}
